import type {
  FC,
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  RefObject,
} from 'react';
import type React from 'react';
import { createContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError, AxiosResponse } from 'axios';
import { useAtom, useAtomValue } from 'jotai';

import type {
  ChatDialog,
  ChatMessage,
  VirtualAssistantDialogResponse,
} from 'src/types/chatbot';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { chatbotChatAtomFamily } from 'src/components/chatbot/atoms';
import {
  CHATBOT_ROLE,
  USER_ROLE,
  WELCOME_MESSAGE_KEYWORD,
} from 'src/components/chatbot/constants';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { dashboardContentLoading } from 'src/components/dashboard/DashboardPage/atom';
import useDashboardFilters from 'src/components/dashboard/hooks/useDashboardFilters';

interface VirtualAssistantContextValue {
  chatDialog: ChatDialog;
  chatHistoryRef?: RefObject<HTMLElement>;
  error: {
    msg?: string;
    userMsg?: ChatMessage;
    queryParams?: object;
  };
  handleInputSubmit: (
    e?: FormEvent,
    queryParams?: object,
    userDisplayMessage?: string,
  ) => void;
  handleTextAreaKeyDown: (event: ReactKeyboardEvent) => void;
  isLoading: boolean;
  scrollToBottom: () => void;
  setInputValue: (value: string) => void;
  errorHandler: () => void;
}

export const VirtualAssistantContext =
  createContext<VirtualAssistantContextValue>({
    chatDialog: [],
    chatHistoryRef: undefined,
    error: {},
    handleInputSubmit: () => {},
    handleTextAreaKeyDown: () => {},
    isLoading: false,
    scrollToBottom: () => {},
    setInputValue: () => {},
    errorHandler: () => {},
  });

interface VirtualAssistantContextProviderProps {
  chatRoomId: string | number;
  newMessageCallback?: () => void;
  children?: ReactNode;
}

export const VirtualAssistantContextProvider: FC<
  VirtualAssistantContextProviderProps
> = ({ chatRoomId, newMessageCallback, children }) => {
  const { dashboardConfig } = useDashboardContext();
  const { i18n, t } = useTranslation('chatbot');
  const { flatFilters } = useDashboardFilters();

  const [isLoading, setIsLoading] = useState(false);
  const [chatDialog, setChatDialog] = useAtom(
    chatbotChatAtomFamily(chatRoomId),
  );
  const contentLoading = useAtomValue(dashboardContentLoading);
  const sessionsCreatedRef = useRef(false);
  const chatHistoryRef = useRef<HTMLElement>(null);
  const inputValueRef = useRef('');
  const setInputValue = (value: string) => (inputValueRef.current = value);
  const [error, setError] = useState<{
    msg?: string;
    userMsg?: ChatMessage;
    queryParams?: object;
    errorType?: 'sessionCreate' | 'sessionDelete' | 'question';
  }>({});

  // Management summary (retrieved via question endpoint) is to be used as introduction
  useEffect(() => {
    if (!contentLoading && !sessionsCreatedRef.current) {
      handleSessionsSubmit();
    }
  }, [contentLoading]);

  useEffect(
    () => () => {
      // Reset chat dialog on unmount and close the session
      setChatDialog([]);
      setError({});
      inputValueRef.current = '';
      HttpService.delete(apiPaths.VIRTUAL_ASSISTANT.SESSIONS_DELETE, {});
    },
    [],
  );

  const scrollToBottom = () => {
    const chatHistory = chatHistoryRef.current;
    if (chatHistory && chatHistory.scrollTo) {
      // use a timeout to set the scroll action at the end of the execution queue
      setTimeout(
        () =>
          chatHistory.scrollTo({
            top: chatHistory.scrollHeight,
            left: 0,
            behavior: 'smooth',
          }),
        0,
      );
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatDialog]);

  const handleSessionsSubmit = () => {
    if (!dashboardConfig) return;

    setIsLoading(true);
    setError({});

    HttpService.post(apiPaths.VIRTUAL_ASSISTANT.SESSIONS_CREATE, {
      add_welcome_message: true,
      dashboard_id: dashboardConfig.id,
      language: i18n.language,
      filters: flatFilters,
    })
      .then(({ data }: AxiosResponse<{ response: string }>) => {
        sessionsCreatedRef.current = true;
        if (data?.response) {
          newMessageCallback && newMessageCallback();
          setChatDialog((prevState) => [
            ...prevState.slice(0, -1),
            {
              content: '',
              role: CHATBOT_ROLE,
              displayMessage: t('virtualAssistant.introduction'),
              highlight: true,
            },
            {
              content: '',
              role: CHATBOT_ROLE,
              displayMessage: data.response,
            },
          ]);
        }
      })
      .catch((error: AxiosError<{ detail?: string }>) => {
        const errorMsg =
          error.response?.data?.detail || 'Virtual assistant error';
        setError({ msg: errorMsg, errorType: 'sessionCreate' });
      })
      .finally(() => {
        inputValueRef.current = '';
        setIsLoading(false);
      });
  };

  const handleInputSubmit = async (
    e?: React.FormEvent,
    queryParams?: object,
    userDisplayMessage?: string,
  ) => {
    e?.preventDefault();
    if (!inputValueRef.current) return;

    setIsLoading(true);
    setError({});
    const newUserMessage: ChatMessage = {
      content: inputValueRef.current,
      role: USER_ROLE,
      displayMessage: userDisplayMessage,
    };
    setChatDialog((prevState) => [...prevState, newUserMessage]);

    try {
      const result: AxiosResponse<VirtualAssistantDialogResponse> =
        await HttpService.post(
          apiPaths.VIRTUAL_ASSISTANT.QUESTION,
          { user_input: inputValueRef.current },
          queryParams,
        );
      const { data } = result;
      if (data?.response) {
        newMessageCallback && newMessageCallback();
        const isSummary =
          data.user_input == WELCOME_MESSAGE_KEYWORD ||
          inputValueRef.current == WELCOME_MESSAGE_KEYWORD;
        setChatDialog((prevState) => [
          ...prevState.slice(0, -1),
          ...(isSummary
            ? []
            : [
                data.user_input
                  ? {
                      content: data.user_input,
                      role: USER_ROLE,
                      displayMessage: '',
                    }
                  : newUserMessage,
              ]),
          {
            content: isSummary ? '' : data.response,
            role: CHATBOT_ROLE,
            displayMessage: isSummary ? data.response : '',
          },
        ]);
        inputValueRef.current = '';
      } else {
        setError({
          msg: 'no virtual assistant response',
          userMsg: newUserMessage,
          queryParams: queryParams,
          errorType: 'question',
        });
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      const errorMsg = axiosError.response?.data?.detail || 'unknown error';
      setError({
        msg: errorMsg,
        userMsg: newUserMessage,
        queryParams: queryParams,
        errorType: 'question',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextAreaKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInputSubmit();
    }
  };

  const errorHandler = () => {
    if (error?.errorType === 'sessionCreate' || !sessionsCreatedRef.current) {
      handleSessionsSubmit();
    } else {
      handleInputSubmit(
        undefined,
        error.queryParams,
        error.userMsg?.displayMessage,
      );
    }
  };

  return (
    <VirtualAssistantContext.Provider
      value={{
        chatDialog,
        chatHistoryRef,
        error,
        handleInputSubmit,
        handleTextAreaKeyDown,
        isLoading,
        scrollToBottom,
        setInputValue,
        errorHandler,
      }}
    >
      {children}
    </VirtualAssistantContext.Provider>
  );
};
